// Procedural Web Audio engine: original chiptune/lo-fi loops + SFX. No external files, no licensing.
const midi = (n) => 440 * Math.pow(2, (n - 69) / 12);

// Patterns: 16 steps per bar. Notes as MIDI numbers, null = rest.
const TRACKS = {
  lobby: {
    bpm: 84,
    chords: [[57, 60, 64], [53, 57, 60], [48, 52, 55], [55, 59, 62]], // Am F C G (lo-fi, mellow)
    bass: [45, 41, 36, 43],
    lead: [
      [76, null, 72, null, 74, null, 76, 79, null, null, 76, null, 74, null, 72, null],
      [72, null, null, 69, null, 72, null, 74, null, null, null, 72, null, 69, null, null],
      [67, null, 72, null, 76, null, 74, null, 72, null, null, 67, null, 69, null, 71],
      [74, null, 71, null, 67, null, null, 69, 71, null, 74, null, null, null, null, null],
    ],
    leadType: "square", leadGain: 0.045, leadCut: 1500, padGain: 0.035, bassGain: 0.09, hat: [4, 12], kick: [],
  },
  play: {
    bpm: 132,
    chords: [[52, 55, 59], [48, 52, 55], [50, 54, 57], [47, 51, 54]], // Em C D B
    bass: [40, 36, 38, 35],
    lead: [
      [76, 79, 83, 79, 76, 79, 83, 79, 76, 79, 83, 79, 78, 79, 83, 86],
      [72, 76, 79, 76, 72, 76, 79, 76, 72, 76, 79, 76, 74, 76, 79, 84],
      [74, 78, 81, 78, 74, 78, 81, 78, 74, 78, 81, 78, 76, 78, 81, 86],
      [71, 75, 78, 75, 71, 75, 78, 75, 71, 75, 78, 75, 73, 75, 78, 83],
    ],
    leadType: "square", leadGain: 0.05, leadCut: 2600, padGain: 0.025, bassGain: 0.1, hat: [2, 6, 10, 14], kick: [0, 4, 8, 12],
  },
};

class Engine {
  constructor() {
    this.ctx = null; this.unlocked = false;
    this.soundOn = true; this.musicOn = true; this.volume = 0.8;
    this.track = null; this.timer = null; this.step = 0; this.nextTime = 0;
    this.listeners = new Set();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) this._stopLoop(); else this._syncMusic();
      });
    }
  }
  onChange(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); }
  _emit() { this.listeners.forEach((f) => f()); }

  unlock() {
    if (!this.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      this.ctx = new AC();
      this.master = this.ctx.createGain(); this.master.gain.value = this.volume; this.master.connect(this.ctx.destination);
      this.sfxBus = this.ctx.createGain(); this.sfxBus.connect(this.master);
      this.musicBus = this.ctx.createGain(); this.musicBus.gain.value = 0.7; this.musicBus.connect(this.master);
      this.noise = this._noiseBuffer();
    }
    if (this.ctx.state === "suspended") this.ctx.resume();
    this.unlocked = true; this._syncMusic(); this._emit();
  }
  setPrefs({ sound, music, volume = 80, muted = false }) {
    this.soundOn = !!sound && !muted; this.musicOn = !!music && !muted;
    this.volume = Math.min(100, Math.max(0, volume)) / 100;
    if (this.master) this.master.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.02);
    this._syncMusic(); this._emit();
  }
  setTrack(name) { if (this.track === name) return; this.track = name; this.step = 0; this._stopLoop(); this._syncMusic(); }

  _noiseBuffer() {
    const b = this.ctx.createBuffer(1, this.ctx.sampleRate * 0.5, this.ctx.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    return b;
  }
  _tone({ type = "square", f0, f1, t0, dur, gain = 0.1, bus, cut }) {
    const o = this.ctx.createOscillator(); o.type = type;
    o.frequency.setValueAtTime(f0, t0);
    if (f1) o.frequency.exponentialRampToValueAtTime(f1, t0 + dur);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0); g.gain.exponentialRampToValueAtTime(gain, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    let out = g;
    if (cut) { const lp = this.ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = cut; g.connect(lp); out = lp; }
    o.connect(g); out.connect(bus || this.sfxBus); o.start(t0); o.stop(t0 + dur + 0.05);
  }
  _hiss({ t0, dur, gain = 0.05, f0 = 6000, f1, q = 1, bus }) {
    const s = this.ctx.createBufferSource(); s.buffer = this.noise;
    const bp = this.ctx.createBiquadFilter(); bp.type = "bandpass"; bp.Q.value = q;
    bp.frequency.setValueAtTime(f0, t0); if (f1) bp.frequency.exponentialRampToValueAtTime(f1, t0 + dur);
    const g = this.ctx.createGain(); g.gain.setValueAtTime(gain, t0); g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    s.connect(bp); bp.connect(g); g.connect(bus || this.sfxBus); s.start(t0); s.stop(t0 + dur + 0.05);
  }

  sfx(name) {
    if (!this.unlocked || !this.soundOn || !this.ctx) return;
    const t = this.ctx.currentTime;
    const T = (o) => this._tone({ t0: t, ...o });
    switch (name) {
      case "tap": T({ type: "sine", f0: 640, f1: 320, dur: 0.07, gain: 0.12 }); break;
      case "correct": T({ f0: midi(84), dur: 0.08, gain: 0.08 }); this._tone({ t0: t + 0.08, f0: midi(88), dur: 0.12, gain: 0.08 }); break;
      case "wrong": T({ type: "sawtooth", f0: 220, f1: 110, dur: 0.28, gain: 0.08, cut: 900 }); break;
      case "tick": T({ f0: 1200, dur: 0.045, gain: 0.07 }); break;
      case "fanfare": [72, 76, 79, 84].forEach((n, i) => this._tone({ t0: t + i * 0.11, f0: midi(n), dur: i === 3 ? 0.5 : 0.14, gain: 0.09 }));
        [60, 64, 67].forEach((n) => this._tone({ t0: t + 0.33, type: "triangle", f0: midi(n), dur: 0.55, gain: 0.05 })); break;
      case "coin": T({ type: "sine", f0: 988, dur: 0.07, gain: 0.12 }); this._tone({ t0: t + 0.07, type: "sine", f0: 1319, dur: 0.22, gain: 0.12 }); break;
      case "streak": T({ type: "sine", f0: 400, f1: 1800, dur: 0.35, gain: 0.1 }); this._hiss({ t0: t + 0.1, dur: 0.3, gain: 0.03, f0: 4000, f1: 9000 });
        [79, 83, 86, 91].forEach((n, i) => this._tone({ t0: t + 0.2 + i * 0.06, f0: midi(n), dur: 0.1, gain: 0.05 })); break;
      case "whoosh": this._hiss({ t0: t, dur: 0.45, gain: 0.12, f0: 300, f1: 3500, q: 0.8 }); break;
      default: break;
    }
  }

  // --- music sequencer (lookahead scheduler) ---
  _syncMusic() {
    const want = this.unlocked && this.musicOn && this.track && this.ctx && !document.hidden;
    if (want && !this.timer) { this.nextTime = this.ctx.currentTime + 0.05; this.timer = setInterval(() => this._schedule(), 25); }
    else if (!want) this._stopLoop();
  }
  _stopLoop() { if (this.timer) { clearInterval(this.timer); this.timer = null; } }
  _schedule() {
    const tr = TRACKS[this.track]; if (!tr) return;
    const stepDur = 60 / tr.bpm / 4;
    while (this.nextTime < this.ctx.currentTime + 0.12) {
      this._playStep(tr, this.step, this.nextTime, stepDur);
      this.nextTime += stepDur; this.step = (this.step + 1) % 64;
    }
  }
  _playStep(tr, step, t, sd) {
    const bar = Math.floor(step / 16), s = step % 16, bus = this.musicBus;
    if (s === 0) tr.chords[bar].forEach((n) => this._tone({ t0: t, type: "sine", f0: midi(n), dur: sd * 15.5, gain: tr.padGain, bus }));
    if (s % 4 === 0) this._tone({ t0: t, type: "triangle", f0: midi(tr.bass[bar]), dur: sd * 3.6, gain: tr.bassGain, bus });
    const n = tr.lead[bar][s];
    if (n != null) this._tone({ t0: t, type: tr.leadType, f0: midi(n), dur: sd * 1.8, gain: tr.leadGain, bus, cut: tr.leadCut });
    if (tr.hat.includes(s)) this._hiss({ t0: t, dur: 0.04, gain: 0.03, f0: 8000, q: 2, bus });
    if (tr.kick.includes(s)) this._tone({ t0: t, type: "sine", f0: 150, f1: 45, dur: 0.12, gain: 0.16, bus });
  }
}

const engine = new Engine();
if (typeof window !== "undefined") window.__ppAudio = engine;
export default engine;
